import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiTrash2, FiPlus, FiEdit2, FiCheck, FiX, FiLoader, FiCopy, FiRefreshCw, FiBookmark, FiAlertCircle } = FiIcons;

const MenuTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    price: 18.50
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // 🔥 ROBUST: Test connection and load templates
  const loadTemplates = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('📋 Loading templates...');

      // Test connection first
      const { data: testData, error: testError } = await supabase
        .from('menu_templates_a7b9c8d6e5')
        .select('count')
        .limit(1);

      if (testError) {
        console.error('❌ Template connection test failed:', testError);
        throw testError;
      }

      // Load actual data
      const { data, error } = await supabase
        .from('menu_templates_a7b9c8d6e5')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Template load error:', error);
        throw error;
      }

      console.log(`✅ ${data?.length || 0} templates loaded`);
      setTemplates(data || []);
    } catch (error) {
      console.error('💥 Error loading templates:', error);
      setError(`Fehler beim Laden der Templates: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Beschreibung ist erforderlich';
    }

    if (!formData.image.trim()) {
      newErrors.image = 'Bild URL ist erforderlich';
    } else if (!formData.image.match(/^https?:\/\/.+/)) {
      newErrors.image = 'Bild URL muss mit http:// oder https:// beginnen';
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'Preis muss größer als 0 sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (editingTemplate) {
        // Update template
        const { error } = await supabase
          .from('menu_templates_a7b9c8d6e5')
          .update({
            name: formData.name,
            description: formData.description,
            image: formData.image,
            price: formData.price,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingTemplate.id);

        if (error) throw error;
      } else {
        // Create new template
        const { error } = await supabase
          .from('menu_templates_a7b9c8d6e5')
          .insert({
            name: formData.name,
            description: formData.description,
            image: formData.image,
            price: formData.price,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      // Reset form
      setFormData({
        name: '',
        description: '',
        image: '',
        price: 18.50
      });
      setIsCreating(false);
      setEditingTemplate(null);
      setErrors({});

      // Reload templates
      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      setError(`Fehler beim Speichern: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      image: template.image,
      price: template.price
    });
    setIsCreating(true);
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Sind Sie sicher, dass Sie dieses Template löschen möchten?')) return;

    try {
      const { error } = await supabase
        .from('menu_templates_a7b9c8d6e5')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      setError(`Fehler beim Löschen: ${error.message}`);
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      image: '',
      price: 18.50
    });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) || 0 : value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const copyToClipboard = (template) => {
    const templateData = {
      name: template.name,
      description: template.description,
      image: template.image,
      price: template.price
    };
    navigator.clipboard.writeText(JSON.stringify(templateData, null, 2));
    alert('Template-Daten in Zwischenablage kopiert!');
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Menü-Templates</h1>
          <p className="text-gray-600">Speichern und verwalten Sie wiederverwendbare Menü-Vorlagen</p>
        </div>
        <div className="flex space-x-3">
          <motion.button
            onClick={loadTemplates}
            disabled={isLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-200 transition-colors duration-300 flex items-center space-x-2 ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            <SafeIcon icon={FiRefreshCw} className={isLoading ? 'animate-spin' : ''} />
            <span>Aktualisieren</span>
          </motion.button>

          <motion.button
            onClick={() => setIsCreating(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-primary-500 text-white px-4 py-2 rounded-xl font-semibold hover:bg-primary-600 transition-colors duration-300 flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Neues Template</span>
          </motion.button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
          <SafeIcon icon={FiAlertCircle} className="mr-2" />
          <div>
            <p className="font-medium">Fehler</p>
            <p className="text-sm">{error}</p>
            <button
              onClick={loadTemplates}
              className="text-sm underline hover:no-underline mt-1"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      )}

      {/* Template Form */}
      {isCreating && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-md p-6 mb-8"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            {editingTemplate ? 'Template bearbeiten' : 'Neues Template erstellen'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Template Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="z.B. Schweinemedaillons mit Kräuterbutter"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Beschreibung *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Detaillierte Beschreibung des Menüs"
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>

            {/* Image URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bild URL *
              </label>
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.image ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="https://beispiel.com/bild.jpg"
              />
              {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image}</p>}
              {formData.image && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Vorschau:</p>
                  <img
                    src={formData.image}
                    alt="Vorschau"
                    className="w-32 h-24 object-cover rounded-lg border border-gray-300"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/300x200?text=Bild+nicht+verfügbar';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preis (CHF) *
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                step="0.10"
                min="0"
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.price ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="18.50"
              />
              {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
            </div>

            {/* Buttons */}
            <div className="flex space-x-4">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex-1 flex items-center justify-center space-x-2 bg-primary-500 text-white py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors duration-300 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                    <span>Wird gespeichert...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} />
                    <span>{editingTemplate ? 'Aktualisieren' : 'Speichern'}</span>
                  </>
                )}
              </motion.button>

              <motion.button
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 flex items-center justify-center space-x-2 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors duration-300"
              >
                <SafeIcon icon={FiX} />
                <span>Abbrechen</span>
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Templates Grid */}
      {isLoading ? (
        <div className="bg-white rounded-2xl shadow-md p-8 text-center">
          <SafeIcon icon={FiLoader} className="mx-auto text-4xl text-primary-500 animate-spin mb-4" />
          <p className="text-gray-600">Templates werden geladen...</p>
        </div>
      ) : templates.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              <img
                src={template.image}
                alt={template.name}
                className="w-full h-48 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/300x200?text=Bild+nicht+verfügbar';
                }}
              />
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                  {template.name}
                </h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                  {template.description}
                </p>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xl font-bold text-primary-600">
                    CHF {template.price.toFixed(2)}
                  </span>
                  <div className="flex items-center text-gray-500 text-xs">
                    <SafeIcon icon={FiBookmark} className="mr-1" />
                    <span>Template</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <motion.button
                    onClick={() => copyToClipboard(template)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center"
                    title="Template kopieren"
                  >
                    <SafeIcon icon={FiCopy} />
                  </motion.button>

                  <motion.button
                    onClick={() => handleEdit(template)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center"
                    title="Template bearbeiten"
                  >
                    <SafeIcon icon={FiEdit2} />
                  </motion.button>

                  <motion.button
                    onClick={() => handleDelete(template.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center"
                    title="Template löschen"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-md p-8 text-center">
          <SafeIcon icon={FiBookmark} className="mx-auto text-4xl text-gray-400 mb-4" />
          <p className="text-gray-500 mb-4">Noch keine Templates gespeichert</p>
          <motion.button
            onClick={() => setIsCreating(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700"
          >
            <SafeIcon icon={FiPlus} />
            <span>Erstes Template erstellen</span>
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default MenuTemplates;